import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import type { TicketFilterAnfrage, TicketKarte, Unternehmen } from "@kundeninfo/api-client";
import { Auswahl, Optionen } from "@kundeninfo/ui/me-design";
import { Avatar } from "./Avatar.js";
import type { AvatarPerson } from "./Avatar.js";
import type { BoardFilter } from "./boardSpeicher.js";
import type { TicketsClient } from "./ticketsClient.js";

/**
 * DIE FILTER DES BOARDS (Spec „Board“ 115, F35) — Unternehmen, Kunde, Zuständig (mit Avatar
 * daneben, F48) und „nur verknüpfte“. Board und Liste teilen sie (Spec „Liste“: „Filter wie im
 * Board“, Ruling A4) samt Gedächtnis (`boardSpeicher.ts`). Gibt nur die Elemente zurück; die
 * Werkzeugleiste drumherum gehört der Ansicht. Kunden kommen aus den gezeigten Tickets, Personen
 * aus `listTicketAssignees` (Rückfall: die Zuständigen der Tickets).
 */

export function filterAnfrage(f: BoardFilter): TicketFilterAnfrage {
  return {
    ...(f.company !== "ALLE" ? { company: f.company } : {}),
    ...(f.customer !== "" ? { customer: f.customer } : {}),
    ...(f.assignee !== "" ? { assignee: f.assignee } : {}),
    ...(f.linked ? { linked: true } : {}),
  };
}

export type TicketFilterleisteProps = {
  client: Pick<TicketsClient, "listTicketAssignees">; companies: readonly Unternehmen[];
  /** Die eigene Person für „Ich“; ohne sie entfällt die Wahl. */
  ich: { id: string; name: string } | null;
  tickets: readonly TicketKarte[]; filter: BoardFilter; onFilter: (f: Partial<BoardFilter>) => void;
};

export function TicketFilterleiste(p: TicketFilterleisteProps): ReactElement {
  // Der Name des gewählten Kunden nur im Speicher der Sitzung (nie im Browserspeicher).
  const [kundeName, setKundeName] = useState("");
  const [personen, setPersonen] = useState<readonly AvatarPerson[]>([]);
  const ichId = p.ich?.id ?? null;

  // Wer zuständig sein kann (mit Mail für den Steckbrief, F48) — einmal beim Öffnen; ohne Antwort
  // bleiben die Zuständigen der Tickets.
  useEffect(() => {
    let aktiv = true;
    p.client.listTicketAssignees()
      .then((r) => { if (aktiv) setPersonen(r.items); })
      .catch(() => { /* Rückfall: die Zuständigen der Tickets */ });
    return () => { aktiv = false; };
  }, [p.client]);

  const kunden = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of p.tickets) for (const c of t.customers) m.set(c.id, c.name);
    if (p.filter.customer !== "" && !m.has(p.filter.customer)) {
      m.set(p.filter.customer, kundeName !== "" ? kundeName : "gewählter Kunde");
    }
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [p.tickets, p.filter.customer, kundeName]);
  const zustaendige = useMemo(() => {
    const m = new Map<string, AvatarPerson>();
    for (const t of p.tickets) if (t.assignee !== null) m.set(t.assignee.id, t.assignee);
    for (const u of personen) m.set(u.id, u);
    if (p.filter.assignee !== "" && p.filter.assignee !== "none" && !m.has(p.filter.assignee)) {
      m.set(p.filter.assignee, { id: p.filter.assignee, name: "gewählte Person" });
    }
    if (ichId !== null) m.delete(ichId);
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [p.tickets, personen, p.filter.assignee, ichId]);
  const ichPerson: AvatarPerson | null = useMemo(() => {
    if (p.ich === null) return null;
    return personen.find((u) => u.id === p.ich!.id) ?? { id: p.ich.id, name: p.ich.name };
  }, [personen, p.ich]);

  const gewaehlt = p.filter.assignee === "" ? undefined
    : p.filter.assignee === "none" ? null
    : p.filter.assignee === ichId && ichPerson !== null ? ichPerson
    : zustaendige.find((u) => u.id === p.filter.assignee) ?? { id: p.filter.assignee, name: "gewählte Person" };

  return (
    <>
      <Optionen beschriftung="Unternehmen" darstellung="segmente" richtung="waagerecht" wert={p.filter.company}
        onGeaendert={(d) => p.onFilter({ company: d.wert ?? "ALLE" })}>
        <option value="ALLE">Alle</option>
        {p.companies.map((c) => <option key={c.id} value={c.id}>{c.shortCode}</option>)}
      </Optionen>
      <Auswahl kompakt beschriftung="Kunde" wert={p.filter.customer}
        onGeaendert={(d) => {
          setKundeName(kunden.find(([id]) => id === d.wert)?.[1] ?? "");
          p.onFilter({ customer: d.wert });
        }}>
        <option value="">Alle Kunden</option>
        {kunden.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
      </Auswahl>
      <Auswahl kompakt beschriftung="Zuständig" wert={p.filter.assignee}
        onGeaendert={(d) => p.onFilter({ assignee: d.wert })}>
        <option value="">Alle</option>
        {ichId !== null && <option value={ichId}>Ich</option>}
        <option value="none">Niemand</option>
        {zustaendige.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </Auswahl>
      {/* F48: die gewählte Person im Avatar daneben — me-auswahl hat (noch) keinen Bild-Slot
          in seinen Optionen (Wunsch an me-design). */}
      {gewaehlt !== undefined && <Avatar ich={ichId !== null && p.filter.assignee === ichId} person={gewaehlt} />}
      <Optionen mehrfach beschriftung="Verknüpfung" wert={p.filter.linked ? ["ja"] : []}
        onGeaendert={(d) => p.onFilter({ linked: (d.werte ?? []).includes("ja") })}>
        <option value="ja">nur verknüpfte</option>
      </Optionen>
    </>
  );
}
